import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { GraduationCap, Cross } from "lucide-react";
import { toast } from "sonner";
import { verseOfTheDay } from "@/lib/scripture";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const verse = verseOfTheDay();

  useEffect(() => {
    if (user) navigate(user.role === "Parent" ? "/parent" : "/student", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await login(email.trim(), password);
    setBusy(false);
    if (res.ok) {
      toast.success(`Welcome back, ${res.user.first_name}!`);
      navigate(res.user.role === "Parent" ? "/parent" : "/student");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block grain overflow-hidden">
        <img
          src="/family-photo.jpg?v=2"
          alt="The Sposato family" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#3D405B]/70 to-transparent" />
        <div className="absolute bottom-0 p-12 text-white">
          <h2 className="font-head text-4xl font-bold leading-tight">A whole school,<br />rooted in faith.</h2>
          <p className="mt-4 text-white/85 max-w-md leading-relaxed">Raising up children in wisdom and truth.</p>
          <div className="mt-6 flex items-start gap-3 max-w-md border-t border-white/20 pt-5">
            <Cross size={16} className="mt-1 shrink-0 text-white/80" />
            <p className="text-sm text-white/85 italic">"{verse.text}" <span className="stat not-italic text-white/70">— {verse.ref}</span></p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-3 mb-8">
            <span className="grid place-items-center w-11 h-11 rounded-2xl bg-[#E07A5F] text-white"><GraduationCap size={22} /></span>
            <span className="font-head font-bold text-xl text-[#3D405B]">Rising Force Academy</span>
          </div>
          <h1 className="font-head text-3xl font-bold text-[#3D405B]">Sign in</h1>
          <p className="text-[#6B7280] mt-1 mb-8">Welcome to your family's classroom.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-[#3D405B]">Email</label>
              <input data-testid="login-email" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoCapitalize="none" autoCorrect="off" spellCheck={false} inputMode="email"
                className="mt-1 w-full rounded-xl border border-[#EAE6DF] bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-[#E07A5F]"
                placeholder="you@risingforceacademy.com" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#3D405B]">Password</label>
              <input data-testid="login-password" type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full rounded-xl border border-[#EAE6DF] bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-[#E07A5F]"
                placeholder="••••••••" />
            </div>
            <button data-testid="login-submit" disabled={busy}
              className="w-full rounded-full bg-[#E07A5F] text-white py-3 font-semibold hover:bg-[#c96851] transition-colors disabled:opacity-60">
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
